import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import ImagemProduto from '@/components/feature/ImagemProduto';
import type { ProdutoPainel } from '@/pages/fichas/types';

interface ProdutoBarraca {
  produto_id: number;
  nome: string;
  emoji: string | null;
  imagem_slug: string | null;
  imagem_url: string | null;
  esgotado: boolean;
  disponivel: number | null;
}

type NivelAlerta = 'amarelo' | 'vermelho';

interface Props {
  pontoId: number;
  eventoId: number | null;
  aoFechar: () => void;
  aoSalvo: () => void;
}

// Lista de produtos da barraca para o operador: marcar esgotado e ver alertas
// de estoque (restam poucos). Semaforo vem de loja_painel (direção); se não
// tiver acesso, cai para o `acabando` da vitrine.
export default function ProdutosBarraca({ pontoId, eventoId, aoFechar, aoSalvo }: Props) {
  const [produtos, setProdutos] = useState<ProdutoBarraca[]>([]);
  const [alertas, setAlertas] = useState<Map<number, NivelAlerta>>(new Map());
  const [carregando, setCarregando] = useState(true);
  const [trocandoId, setTrocandoId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      try {
        const { data: prods, error: errProds } = await supabase.rpc('loja_produtos_do_ponto', {
          p_ponto: pontoId,
        });
        if (errProds) throw errProds;
        if (!ativo) return;
        setProdutos((prods as ProdutoBarraca[]) || []);

        // Alertas: tenta loja_painel (semaforo); senão, a vitrine (acabando).
        const mapa = new Map<number, NivelAlerta>();
        if (eventoId != null) {
          try {
            const { data: painelData } = await supabase.rpc('loja_painel', { p_evento: eventoId });
            const porProduto = ((painelData as { por_produto?: ProdutoPainel[] } | null)?.por_produto) ?? [];
            porProduto.forEach((p) => {
              if (p.ponto_id !== pontoId) return;
              if (p.semaforo === 'vermelho') mapa.set(p.produto_id, 'vermelho');
              else if (p.semaforo === 'amarelo') mapa.set(p.produto_id, 'amarelo');
            });
          } catch {
            try {
              const { data: vit } = await supabase.rpc('loja_vitrine', { p_evento: eventoId });
              const pontos = ((vit as { pontos?: { ponto_id: number; produtos: { produto_id: number; acabando: boolean }[] }[] } | null)?.pontos) ?? [];
              const ponto = pontos.find((p) => p.ponto_id === pontoId);
              ponto?.produtos.forEach((p) => {
                if (p.acabando) mapa.set(p.produto_id, 'amarelo');
              });
            } catch {
              /* segue sem alertas */
            }
          }
        }
        if (ativo) setAlertas(mapa);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e));
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [pontoId, eventoId]);

  async function alternarEsgotado(p: ProdutoBarraca) {
    setTrocandoId(p.produto_id);
    setErro(null);
    try {
      const { error } = await supabase.rpc('loja_marcar_esgotado', {
        p_produto: p.produto_id,
        p_esgotado: !p.esgotado,
      });
      if (error) throw error;
      setProdutos((lista) =>
        lista.map((x) => (x.produto_id === p.produto_id ? { ...x, esgotado: !x.esgotado } : x)),
      );
      aoSalvo();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocandoId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <div className="flex max-h-[85vh] w-full max-w-[520px] flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h3 className="text-base font-semibold text-foreground-950">Produtos da barraca</h3>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {erro && <p className="mb-3 text-sm text-danger-600">{erro}</p>}

          {carregando ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-secondary-100" />
              ))}
            </div>
          ) : produtos.length === 0 ? (
            <p className="py-6 text-center text-sm text-secondary-500">
              Nenhum produto nesta barraca.
            </p>
          ) : (
            <ul className="divide-y divide-secondary-100">
              {produtos.map((p) => {
                const alerta = alertas.get(p.produto_id);
                const trocando = trocandoId === p.produto_id;
                return (
                  <li key={p.produto_id} className="flex items-center gap-3 py-3">
                    <ImagemProduto
                      imagemUrl={p.imagem_url}
                      imagemSlug={p.imagem_slug}
                      emoji={p.emoji}
                      nome={p.nome}
                      tamanho="miniatura"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-medium text-foreground-950">
                        {p.nome}
                      </p>
                      {alerta && p.disponivel != null && (
                        <p
                          className={`mt-0.5 text-xs font-semibold ${
                            alerta === 'vermelho' ? 'text-danger-600' : 'text-warning-700'
                          }`}
                        >
                          restam {p.disponivel} {p.nome}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => alternarEsgotado(p)}
                      disabled={trocando}
                      className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        p.esgotado
                          ? 'border-secondary-300 bg-white text-foreground-800 hover:bg-background-100'
                          : 'border-danger-200 bg-white text-danger-600 hover:bg-danger-50'
                      }`}
                    >
                      {trocando ? '…' : p.esgotado ? 'Voltou' : 'Esgotou'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-t border-secondary-200 px-5 py-3">
          <button
            type="button"
            onClick={aoFechar}
            className="w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Concluir
          </button>
        </div>
      </div>
    </div>
  );
}